import React, { useState, useEffect } from "react";
import { Card, Table, Row, Col, Nav, Form, Button } from "react-bootstrap";
import { Link } from "react-router-dom";
import { WhiteBoxWrapper } from "./Login.style";
import { fetchAPI } from "../utils/fetchAPI";
import * as XLSX from "xlsx";
import { FaFileExcel } from "react-icons/fa";

interface Log {
  Info: string;
  user?: { id: number; name: string; surname: string };
  device?: { id: number; name: string; description: string };
  datelog: string;
}

interface SummaryEntry {
  user: string;
  device: string;
  count: number;
}

interface QuantitySummary {
  [device: string]: number;
}

const SystemLogs = () => {
  const [logs, setLogs] = useState<Log[]>([]);
  const [summary, setSummary] = useState<SummaryEntry[]>([]);
  const [quantitySummary, setQuantitySummary] = useState<QuantitySummary>({});
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [startDate, setStartDate] = useState<string>("");
  const [endDate, setEndDate] = useState<string>("");
  const [noResultsMessage, setNoResultsMessage] = useState<string>("");

  const fetchLogs = async (start?: string, end?: string) => {
    setIsLoading(true);
    setErrorMessage("");
    setNoResultsMessage("");

    try {
      let url = `logs.php`;
      if (start && end) {
        url += `?start_date=${start}&end_date=${end}`;
      }

      const data = await fetchAPI("GET", url);

      if (!data) {
        setNoResultsMessage("Nie znaleziono wyników w podanym przedziale dat.");
        setLogs([]);
        setSummary([]);
        setQuantitySummary({});
        return;
      }

      if (data.success) {
        setLogs(data.logs || []);
        setSummary(data.summary || []);

        const deviceCounts: QuantitySummary = {};
        (data.summary || []).forEach((entry: SummaryEntry) => {
          const count = Number(entry.count); // Konwersja na liczbę
          if (!isNaN(count)) {
            deviceCounts[entry.device] = (deviceCounts[entry.device] || 0) + count;
          }
        });
        setQuantitySummary(deviceCounts);
      } else {
        throw new Error(data.message || "Nie udało się pobrać logów.");
      }
    } catch (error: any) {
      setErrorMessage(error.message || "Wystąpił błąd podczas pobierania danych.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs();
  }, []);

  const handleDateFilter = (e: React.FormEvent) => {
    e.preventDefault();
    fetchLogs(startDate, endDate);
  };

  const generateExcel = () => {
    // Przygotuj dane do eksportu - ogólne logi
    const logsData = logs.map((log) => ({
      Akcja: log.Info,
      Użytkownik: log.user ? `${log.user.name} ${log.user.surname}` : "Nieznany użytkownik",
      Urządzenie: log.device ? `${log.device.name} - ${log.device.description}` : "Brak danych o urządzeniu",
      Data: new Date(log.datelog).toLocaleString(),
    }));

    // Przygotuj dane do eksportu - podsumowanie ilościowe
    const quantityData = Object.entries(quantitySummary).map(([device, total]) => ({
      Urządzenie: device,
      "Liczba wybranych sztuk": total,
    }));

    // Przygotuj dane do eksportu - podsumowanie wydań
    const summaryData = summary.map((entry) => ({
      Użytkownik: entry.user,
      Urządzenie: entry.device,
      "Liczba wybranych sztuk": entry.count,
    }));

    // Utwórz nowy arkusz dla każdej sekcji
    const logsWs = XLSX.utils.json_to_sheet(logsData);
    const quantityWs = XLSX.utils.json_to_sheet(quantityData);
    const summaryWs = XLSX.utils.json_to_sheet(summaryData);

    // Utwórz nową książkę
    const wb = XLSX.utils.book_new();

    // Dodaj arkusze do książki
    XLSX.utils.book_append_sheet(wb, logsWs, "Ogólne logi");
    XLSX.utils.book_append_sheet(wb, quantityWs, "Podsumowanie ilościowe");
    XLSX.utils.book_append_sheet(wb, summaryWs, "Podsumowanie wydań");

    // Zapisz plik
    XLSX.writeFile(wb, "logi_systemowe.xlsx");
  };

  return (
    <div>
      <WhiteBoxWrapper>
        <Row className="align-items-center">
          <Col xs={1}>
            <h2>
              <Nav.Link as={Link} to="/">
                <b>&larr;</b>
              </Nav.Link>
            </h2>
          </Col>
          <Col xs={11}>
            <h2 className="text-center">
              <b>Pełna historia logów systemu</b>
            </h2>
          </Col>
        </Row>
        <Row>
          <Col xs={12} className="d-flex justify-content-end mt-3">
            <Button variant="primary" onClick={generateExcel}>
              <FaFileExcel className="me-2" /> Generuj Excel
            </Button>
          </Col>
        </Row>
      </WhiteBoxWrapper>

      <Card className="p-2 bg-brand mt-4">
        <Form onSubmit={handleDateFilter} className="mb-4">
          <Row>
            <Col xs={6}>
              <Form.Group controlId="startDate">
                <Form.Label>Data początkowa</Form.Label>
                <Form.Control type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
              </Form.Group>
            </Col>
            <Col xs={6}>
              <Form.Group controlId="endDate">
                <Form.Label>Data końcowa</Form.Label>
                <Form.Control type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
              </Form.Group>
            </Col>
            <Col className="text-end">
              <Button variant="dark" type="submit" className="mt-3">
                Filtruj
              </Button>
            </Col>
          </Row>
        </Form>

        {noResultsMessage && <p>{noResultsMessage}</p>}

        {summary.length > 0 && !noResultsMessage && (
          <div className="mb-4">
            <h5>
              <b>Podsumowanie ilościowe</b>
            </h5>
            <Table striped bordered hover responsive>
              <thead>
                <tr>
                  <th>Urządzenie</th>
                  <th>Liczba wybranych sztuk</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(quantitySummary).map(([device, total], index) => (
                  <tr key={index}>
                    <td>{device}</td>
                    <td>{total}</td>
                  </tr>
                ))}
              </tbody>
            </Table>

            <h5>
              <b>Podsumowanie Wydań</b>
            </h5>
            <Table striped bordered hover responsive>
              <thead>
                <tr>
                  <th>Użytkownik</th>
                  <th>Urządzenie</th>
                  <th>Liczba wybranych sztuk</th>
                </tr>
              </thead>
              <tbody>
                {summary.map((entry, index) => (
                  <tr key={index}>
                    <td>{entry.user}</td>
                    <td>{entry.device}</td>
                    <td>{entry.count}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}

        <h5>
          <b>Ogólne logi</b>
        </h5>
        {logs.length > 0 && !noResultsMessage ? (
          <Table striped bordered hover responsive>
            <thead>
              <tr>
                <th>Akcja</th>
                <th>Użytkownik</th>
                <th>Urządzenie</th>
                <th>Data</th>
              </tr>
            </thead>
            <tbody>
              {logs.map((log, index) => (
                <tr key={index}>
                  <td>{log.Info}</td>
                  <td>
                    {log.user ? (
                      <Nav.Link as={Link} to={`/user/${log.user.id}`}>
                        <b>
                          {log.user.name} {log.user.surname}
                        </b>
                      </Nav.Link>
                    ) : (
                      "Nieznany użytkownik"
                    )}
                  </td>
                  <td>
                    {log.device ? (
                      <Nav.Link as={Link} to={`/device/${log.device.id}`}>
                        <b>
                          {log.device.name} - {log.device.description}
                        </b>
                      </Nav.Link>
                    ) : (
                      "Brak danych o urządzeniu"
                    )}
                  </td>
                  <td>{new Date(log.datelog).toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        ) : (
          <p>Brak logów do wyświetlenia.</p>
        )}

        {isLoading && <p>Ładowanie danych...</p>}
        {errorMessage && <p className="error">{errorMessage}</p>}
      </Card>
    </div>
  );
};

export default SystemLogs;
